import React, { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import axios from 'axios';
import { 
  calculateQuestionnaireResults, 
  getQuestionnaireType
} from '../utils/questionnaireTypes';
import RadarChartResults from '../components/results/RadarChartResults';
import ScatterPlotResults from '../components/results/ScatterPlotResults';
import {
  Chart as ChartJS,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';

ChartJS.register(LinearScale, RadialLinearScale, PointElement, LineElement, Tooltip, Legend, Title);

const ResultsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [questionnaireConfig, setQuestionnaireConfig] = useState(null);

  // Pre-populate email if user is authenticated
  useEffect(() => {
    if (isAuthenticated && user && user.email) {
      setEmail(user.email);
    }
  }, [isAuthenticated, user]);

  const loadSavedProgress = useCallback(async () => {
    try {
      if (!isAuthenticated || !user?.id) {
        navigate('/');
        return;
      }

      // Get questionnaire configuration
      const config = getQuestionnaireType(id);
      if (!config) {
        throw new Error(`Configuration not found for questionnaire: ${id}`);
      }
      setQuestionnaireConfig(config);

      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/loadProgress'
        : '/api/loadProgress';

      const response = await axios.get(apiUrl, {
        params: { userId: user.id, questionnaireId: id },
        withCredentials: true,
      });

      if (response.data.progress && response.data.progress.answers) {
        const { answers } = response.data.progress;
        
        // Use the new utility function to calculate results
        const calculatedResults = calculateQuestionnaireResults(id, answers);
        setResults(calculatedResults);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading saved progress:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, id, navigate]);
  
  const calculateResults = useCallback(() => {
    try {
      const { answers } = location.state || {};
      
      // Check if this is a test mode
      const urlParams = new URLSearchParams(window.location.search);
      const isTestMode = urlParams.get('test') === 'true';
      
      if (isTestMode) {
        // Load test data from localStorage
        const testData = localStorage.getItem('testResultsData');
        if (testData) {
          const parsedTestData = JSON.parse(testData);
          
          // Get questionnaire configuration
          const config = getQuestionnaireType(parsedTestData.questionnaireId);
          if (!config) {
            throw new Error(`Configuration not found for questionnaire: ${parsedTestData.questionnaireId}`);
          }
          setQuestionnaireConfig(config);
          
          // Store user name for test
          if (parsedTestData.userName) {
            localStorage.setItem('currentUserName', parsedTestData.userName);
          }
          
          // Use the new utility function to calculate results
          const calculatedResults = calculateQuestionnaireResults(parsedTestData.questionnaireId, parsedTestData.answers);
          setResults(calculatedResults);
          setLoading(false);
          return;
        }
      }
      
      if (!answers) {
        // If no state passed, try to load from saved progress
        loadSavedProgress();
        return;
      }

      // Get questionnaire configuration
      const config = getQuestionnaireType(id);
      if (!config) {
        throw new Error(`Configuration not found for questionnaire: ${id}`);
      }
      setQuestionnaireConfig(config);
      
      // Use the new utility function to calculate results
      const calculatedResults = calculateQuestionnaireResults(id, answers);
      setResults(calculatedResults);
    } catch (error) {
      console.error('Erreur lors du calcul des résultats:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [location.state, navigate, loadSavedProgress, id]);

  useEffect(() => {
    calculateResults();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [calculateResults]);

  const sendResultsEmail = async () => {
    if (!email.trim() || !results) return;

    setSendingEmail(true);

    try {
      console.log('📧 Sending results email...');

      const emailApiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/sendResultsEmail'
        : '/api/sendResultsEmail';

      // Prepare results data based on questionnaire type
      let resultsData;
      if (questionnaireConfig.resultDisplayType === 'radar' && results.scores) {
        resultsData = results.scores.map((score, index) => ({
          category: index + 1,
          name: score.name,
          score: score.score,
          total: score.total || questionnaireConfig.scaleMax,
          count: score.count || 1
        }));
      } else if (questionnaireConfig.resultDisplayType === 'scatter' && results.results) {
        resultsData = results.results.map((result, index) => ({
          category: index + 1,
          name: result.name,
          score: result.average,
          total: 7, // Max scale for orientation conscience
          count: result.count || 1
        }));
      } else {
        resultsData = results.scores || results.results || [];
      }

      await axios.post(emailApiUrl, {
        email: email.trim(),
        userId: user?.id || 'anonymous',
        questionnaireId: questionnaireConfig.id,
        userName: user?.name || localStorage.getItem('currentUserName') || 'Utilisateur',
        results: resultsData,
        questionnaireConfig: questionnaireConfig
      }, {
        withCredentials: true,
        timeout: 30000 // 30 second timeout
      });

      console.log('✅ Email sent successfully');
      setEmailSent(true);
      alert('📧 Résultats envoyés avec succès par email !');

    } catch (error) {
      console.error('❌ Email sending failed:', error);

      // Provide user-friendly error messages
      let errorMessage = 'L\'envoi de l\'email a échoué. ';

      if (error.response) {
        const status = error.response.status;
        if (status === 503) {
          errorMessage += 'Le service email est temporairement indisponible. Veuillez réessayer dans quelques minutes.';
        } else if (status === 400) {
          errorMessage += 'Les données sont invalides. Vérifiez l\'adresse email et réessayez.';
        } else if (status >= 500) {
          errorMessage += 'Erreur serveur. Veuillez contacter le support technique.';
        } else {
          errorMessage += `Erreur HTTP ${status}. Veuillez réessayer.`;
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage += 'L\'envoi a pris trop de temps. Veuillez réessayer.';
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage += 'Problème de connexion réseau. Vérifiez votre connexion.';
      } else {
        errorMessage += 'Erreur inconnue. Veuillez réessayer ou contacter le support.';
      }

      alert(`🚫 ${errorMessage}

Détails techniques: ${error.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="loading">Calcul des résultats...</div>
      </div>
    );
  }

  if (!results || !questionnaireConfig) {
    return (
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="error">
          Aucun résultat disponible. Veuillez refaire le questionnaire.
        </div>
      </div>
    );
  }

  // Determine which results component to render based on questionnaire type
  const renderResults = () => {
    switch (questionnaireConfig.resultDisplayType) {
      case 'radar':
        return <RadarChartResults results={results} questionnaireConfig={questionnaireConfig} />;
      case 'scatter':
        return <ScatterPlotResults results={results} questionnaireConfig={questionnaireConfig} />;
      default:
        return <div>Type de résultat non supporté: {questionnaireConfig.resultDisplayType}</div>;
    }
  };
  
  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '1rem', color: '#2c3e50' }}>
          🎉 Vos Résultats - {questionnaireConfig.name}
        </h1>
        
        <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
          <p style={{ fontSize: '1.1rem', color: '#7f8c8d' }}>
            Félicitations ! Vous avez terminé le questionnaire avec {results.totalQuestions} questions.
          </p>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            🏠 Retour à l'accueil
          </button>
        </div>
      </div>

      {/* Results Display */}
      {renderResults()}

      {/* Email Section */}
      <div className="card" style={{ marginTop: '2rem' }}>
        <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
          📧 Recevoir les résultats par email
        </h3>
        {!emailSent ? (
          <>
            <p style={{ marginBottom: '1rem', color: '#7f8c8d' }}>
              Recevez vos résultats détaillés par email.
            </p>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="email"
                className="form-input"
                placeholder="votre@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{ flex: 1, minWidth: '250px' }}
              />
              <button 
                onClick={sendResultsEmail} 
                disabled={!email.trim() || sendingEmail} 
                className="btn btn-success"
              >
                {sendingEmail ? 'Envoi...' : 'Envoyer les résultats'}
              </button>
            </div>
          </>
        ) : (
          <div className="success">✅ Résultats envoyés avec succès !</div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        {isAuthenticated && (
          <button 
            onClick={() => navigate('/dashboard')} 
            className="btn btn-primary"
            style={{ marginRight: '1rem' }}
          >
            📊 Voir le tableau de bord
          </button>
        )}
      </div>
    </div>
  );
};

export default ResultsPage;
